import { GridFilterModel } from "@mui/x-data-grid";
import { cast, flow, getEnv, Instance, types } from "mobx-state-tree";

import serializeToQueryParams from "../../helpers/QueryParams";
import { endpoints } from "../api/ApiEndpoints";
import { StorageModel } from "../storage/StorageModel";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { Column, ColumnType } from "../../components/datatable/interfaces";
import { sortColumns } from "../../components/datatable/helpers/sortExtensions";
import {
  areCoordinatesValid,
  CardinalPoints,
  Coordinate,
  getBoundingCardinalPoints,
  isBoundingBoxLargerThan,
} from "../../helpers/MapHelpers";
import { MapItem } from "./MapItem";
import { getTranslationKeyForType, SewerObject } from "./SewerObject";
import { moveItemInArray } from "../../helpers/ArrayHelpers";
import { MapItemResponse } from "../../api-models/response/MapItemResponse";
import { HeaderItemResponse } from "../../api-models/response/DataGridResponse";
import { PROFIL_BREITE_CODE, HOHE_LANGE_CODE } from "../../api-models/response/BluemetricFields";
import { SewerObjectType } from "../../generated/SewerObjects";

const defaultVisibleColumns: Column[] = [
  {
    key: "Name",
    name: "general.sewer-objects.list-header.sewer-object-id",
    type: ColumnType.String,
    sortable: true,
    filterable: true,
    containsLink: true,
  },
  {
    key: "Type",
    name: "general.type",
    type: ColumnType.SingleSelect,
    valueOptions: [
      {
        label: getTranslationKeyForType(SewerObjectType.Section),
        value: SewerObjectType.Section.toString(),
      },
      {
        label: getTranslationKeyForType(SewerObjectType.Node),
        value: SewerObjectType.Node.toString(),
      },
      {
        label: getTranslationKeyForType(SewerObjectType.Structure),
        value: SewerObjectType.Structure.toString(),
      },
    ],
    sortable: true,
    filterable: true,
  },
  {
    key: "InspectionsCount",
    name: "general.inspections",
    type: ColumnType.Number,
    sortable: true,
    filterable: true,
  },
];

const esicTableColumns: Column[] = [
  ...defaultVisibleColumns,
  {
    key: "Modified",
    name: "general.last-edit",
    type: ColumnType.DateTime,
    sortable: true,
    filterable: true,
  },
];

interface SewerObjectMapTableResponse {
  totalCount: number;
  header: HeaderItemResponse[];
  rows: MapItemResponse[];
}

const SewerObjectsMapModel = types.model({
  inputProjectId: types.maybe(types.number),
  inputJobId: types.maybe(types.number),
  inputSewerObjects: types.maybe(types.array(types.number)),
  sewerObjectsStorage: types.optional(
    types.compose(
      DataGridStorageModel,
      types
        .model({
          headers: types.maybe(types.frozen<Column[]>()),
          dataLoaded: types.optional(types.boolean, false),
        })
        .views((self) => ({
          get columns() {
            return self.headers ?? [];
          },
        }))
    ),
    {
      autoLoad: false,
      useGridFiltering: true,
      clientSidePagination: true,
      defaultSort: { field: "Name", sort: "asc" },
    }
  ),
  mapItems: types.maybe(types.optional(types.array(types.frozen<MapItem>()), [])),
  gridFilter: types.frozen<GridFilterModel | undefined>(),
  isFilterMapDisabled: true,
  mapNeedsRecomputation: false,
  isDataLoading: false,
  defaultSelectedItem: types.maybe(types.string),
  currentMapStyles: "road",
});

export const SewerObjectsMapStorageModel = types
  .compose(StorageModel, SewerObjectsMapModel)
  .named("SewerObjectsMapStorageModel")
  .actions((self) => {
    const setFilterData = flow(function* (gridFilter: GridFilterModel | undefined) {
      self.isDataLoading = true;
      self.gridFilter = gridFilter;
      self.mapItems = cast(undefined);
      yield self.load();
      self.isDataLoading = false;
    });

    const getHeaders = (response: any): Column[] => {
      const sewerObjectsHeaders = response.header;
      if (!sewerObjectsHeaders.length) return esicTableColumns;

      sewerObjectsHeaders.sort(sortColumns);
      const filteredHeaders = sewerObjectsHeaders.filter(
        (header: any) => header.key !== "Organization" && header.key !== "Project"
      );

      const BMColumns = filteredHeaders
        .filter((header: any) => esicTableColumns.every((x) => x.key !== header.key))
        .map((column: HeaderItemResponse) => {
          return {
            key: column.key,
            name: column.name,
            type: self.sewerObjectsStorage.getColumnType(column.metadata),
            valueOptions: self.sewerObjectsStorage.getColumnPredefinedValueOptions(column.metadata),
            sortable: column.key !== PROFIL_BREITE_CODE && column.key !== HOHE_LANGE_CODE,
            filterable: column.key !== PROFIL_BREITE_CODE && column.key !== HOHE_LANGE_CODE,
          };
        });
      const allColumns = esicTableColumns.concat(BMColumns);

      const modifiedColumnIndex = allColumns.findIndex((x) => x.key === "Modified");
      moveItemInArray(allColumns, modifiedColumnIndex, filteredHeaders.length - 1);

      return allColumns;
    };

    return {
      setDefaultSelectedItem(item: string | undefined) {
        self.defaultSelectedItem = item;
      },
      setCurrentMapStyles(style: string | undefined) {
        self.currentMapStyles = style ?? self.currentMapStyles;
      },
      afterCreate() {
        setFilterData(undefined);
      },
      mapResponse(response: SewerObjectMapTableResponse) {
        self.mapItems = cast(response.rows.map((x: MapItemResponse) => new MapItem(x)));
        if (self.sewerObjectsStorage.dataLoaded) return;

        self.sewerObjectsStorage.headers = getHeaders(response);
        self.sewerObjectsStorage.defaultColumnVisibility = self.sewerObjectsStorage.headers
          .filter((column) => defaultVisibleColumns.every((x) => x.key !== column.key))
          .reduce((columnVisibility, column) => {
            columnVisibility[column.key] = false;
            return columnVisibility;
          }, {});
        self.sewerObjectsStorage.data = response.rows.map((item) => {
          const parentPagePath = self.inputProjectId
            ? `/projects/${self.inputProjectId}`
            : self.inputJobId
            ? `/jobs/${self.inputJobId}`
            : "/";
          return SewerObject._constructorFromMapItem(item, parentPagePath);
        });
        self.sewerObjectsStorage.dataLoaded = true;
      },
      setFilterData,
      getToken: flow(function* () {
        const endpoint = endpoints.get("azure_maps_token");
        const token = yield getEnv(self).api.get(endpoint);
        return token;
      }),
      setIsFilterMapDisabled(isFilterMapDisabled: boolean) {
        self.isFilterMapDisabled = isFilterMapDisabled;
      },
      setMapNeedsRecomputation(mapRecomputation: boolean) {
        self.mapNeedsRecomputation = mapRecomputation;
      },
    };
  })
  .views((self) => ({
    get endpoint() {
      const endpoint = endpoints.get("sewer_objects_map");
      const queryParams = serializeToQueryParams({
        ids: self.inputSewerObjects && self.inputSewerObjects.toString(),
        projectId: self.inputProjectId,
        jobId: self.inputJobId,
      });
      endpoint.queryParams = queryParams;
      return endpoint;
    },
    get canLoadItems() {
      return !!self.inputProjectId || !!self.inputJobId || !!self.inputSewerObjects;
    },
    get mapCenter() {
      return self.mapItems?.find((x) => x.hasGeometry)?.mapCoordinates[0];
    },
    get requestBody(): {} | null {
      return self.gridFilter ?? {};
    },
    get numberOfSewerObjects() {
      return self.mapItems?.length ?? 0;
    },
    get numberOfSewerObjectsWithGeometry() {
      return self.mapItems?.filter((x) => x.hasGeometry).length ?? 0;
    },
    get boundingBox(): CardinalPoints | undefined {
      if (!self.mapItems) return undefined;

      const coordinates: Coordinate[] = self.mapItems
        .filter((item: MapItem) => item.mapCoordinates.length)
        .map((item: MapItem) => {
          const [longitude, latitude] = item.mapCoordinates[0];
          return { longitude, latitude };
        });

      if (areCoordinatesValid(coordinates)) {
        return getBoundingCardinalPoints(coordinates);
      }

      return undefined;
    },
  }))
  .views((self) => ({
    get numberOfSewerObjectsWithoutGeometry() {
      return self.numberOfSewerObjects - self.numberOfSewerObjectsWithGeometry;
    },
    get allSewerObjectsAreWithoutGeometry() {
      return self.numberOfSewerObjectsWithGeometry === 0;
    },
    get someSewerObjectsAreWithoutGeometry() {
      return (
        self.numberOfSewerObjectsWithGeometry > 0 &&
        self.numberOfSewerObjects - self.numberOfSewerObjectsWithGeometry !== 0
      );
    },
    get oneSewerObjectIsWithoutGeometry() {
      return self.numberOfSewerObjects - self.numberOfSewerObjectsWithGeometry === 1;
    },
    get allSewerObjectsAreWithGeometry() {
      return self.numberOfSewerObjects === self.numberOfSewerObjectsWithGeometry;
    },
    get noVisibleSewerObjects() {
      return self.mapItems?.length === 0;
    },
    get isItemsBoundingBoxTooLarge(): boolean {
      const maxDistanceKM = 1000;

      if (self.boundingBox) {
        return isBoundingBoxLargerThan(self.boundingBox!, maxDistanceKM);
      }
      return false;
    },
  }));

export type SewerObjectsMapStorage = Instance<typeof SewerObjectsMapStorageModel>;
